import definitions from './searchFilterContract.json';
export const ROBUST_FILTERS = definitions;
export const ROBUST_NAMES = Object.keys(definitions).filter(key => key !== 'minGarageSpaces');
const legacy = ['state','city','zip','address','county','status','originatingSystemName','minBedrooms','maxBedrooms','minPrice','maxPrice','cooling','foundation','heating','propertySubType','allPropertySubTypes','propertyType','listAgentMlsId','listingKey','hasFireplace','hasFence','hasPool','schoolDistrict','minSqft','maxSqft','stories','minYearBuilt','maxYearBuilt','minBathrooms','maxBathrooms','communityFeatures','minLotSizeAcres','maxLotSizeAcres','priceSort','daysonmarketSort','listingDateSort','newConstruction','hasPrivatePool','waterfront','hasBasement','hasAttachedGarage','centerLat','centerLng','radiusMiles','hasMedia','minListingDate','north','south','east','west'];
const gatedNames = ['priceReduced','priceReductionBasis','minPriceReductionAmount','minPriceReductionPercent','minBathroomsDecimal','maxBathroomsDecimal'];
export const FILTER_NAMES = new Set([...legacy, ...Object.keys(definitions), ...gatedNames]);
const arrayNames = new Set(['propertySubType','communityFeatures','subdivision','originatingSystemName','listingKey']);
const boolNames = new Set(['allPropertySubTypes','hasFireplace','hasFence','hasPool','newConstruction','hasPrivatePool','waterfront','hasBasement','hasAttachedGarage','hasMedia','hasHoa','hasGarage','hasCarport']);
export const present = value => value !== undefined && value !== null && value !== '';
export function searchSnapshot(params = {}, fallbackState) {
  const result = {};
  Object.entries(params || {}).forEach(([raw, value]) => {
    const key = raw === 'property_type' ? 'propertyType' : raw === 'daysOnMarketSort' ? 'daysonmarketSort' : raw;
    if (!FILTER_NAMES.has(key) || !present(value)) return;
    if (arrayNames.has(key) || Array.isArray(value)) {
      const items = (Array.isArray(value) ? value : [value]).filter(present);
      if (items.length) result[key] = [...items];
    } else result[key] = value;
  });
  if (!result.state && !result.city && !result.zip && !result.centerLat && fallbackState) result.state = fallbackState;
  return result;
}
export function searchQuery(params) {
  const query = new URLSearchParams();
  Object.entries(searchSnapshot(params)).forEach(([key,value]) => {
    (Array.isArray(value) ? value : [value]).forEach(item => query.append(key,String(item)));
  });
  return query;
}
export function searchFromQuery(input) {
  const query = input instanceof URLSearchParams ? input : new URLSearchParams(input);
  const result = {};
  for (const key of new Set(query.keys())) {
    if (!FILTER_NAMES.has(key)) continue;
    const all = query.getAll(key);
    result[key] = arrayNames.has(key) || all.length>1 ? all : boolNames.has(key) && ['true','false'].includes(all[0]) ? all[0]==='true' : all[0];
  }
  return searchSnapshot(result);
}
export function mergeSearchEdits(previous, edits) {
  const next = {...searchSnapshot(previous)};
  for (const [key,value] of Object.entries(edits)) {
    if (!present(value) || (Array.isArray(value) && !value.length)) delete next[key];
    else next[key] = value;
  }
  if ('propertyType' in edits && edits.propertyType!==previous.propertyType && !('propertySubType' in edits)) {delete next.propertySubType;next.allPropertySubTypes=true;}
  if ('propertySubType' in edits) {if(next.propertySubType?.length) delete next.allPropertySubTypes;else next.allPropertySubTypes=true;}
  if ('minBedrooms' in edits && !('maxBedrooms' in edits)) delete next.maxBedrooms;
  if ('city' in edits && edits.city !== previous.city) { delete next.zip; if (!('subdivision' in edits)) delete next.subdivision; }
  if ('state' in edits && edits.state !== previous.state) {
    for (const key of ['city','zip','subdivision']) if (!(key in edits)) delete next[key];
  }
  if (('city' in edits && edits.city !== previous.city) || ('state' in edits && edits.state !== previous.state)) {
    for(const key of ['north','south','east','west','centerLat','centerLng','radiusMiles']) if(!(key in edits)) delete next[key];
  }
  return searchSnapshot(next);
}
export function assertValidSearch(params) {
  if(params.allPropertySubTypes && params.propertySubType?.length) throw new Error('Choose specific home types or any home type.');
  const bounds=['north','south','east','west'];
  if(bounds.some(key=>present(params[key]))) {
    if(!bounds.every(key=>present(params[key])&&Number.isFinite(Number(params[key])))||Math.abs(Number(params.north))>90||Math.abs(Number(params.south))>90||Math.abs(Number(params.east))>180||Math.abs(Number(params.west))>180||Number(params.north)<=Number(params.south)||Number(params.east)===Number(params.west)) throw new Error('Choose a valid map area.');
  }
  if (gatedNames.some(key=>present(params[key]))) throw new Error('Price-reduction and fractional bathroom filters are not available yet. Change those requirements to continue.');
  for (const [key,def] of Object.entries(definitions)) {
    const value=params[key]; if (!present(value)) continue;
    if (def.type==='boolean') {
      if (![true,false,'true','false'].includes(value)) throw new Error(`Choose Any, Yes or No for ${def.label}.`);
    } else if (def.type==='string[]') {
      if (!Array.isArray(value) || value.length<1 || value.length>20 || value.some(v=>typeof v!=='string'||!v.trim()||v.length>200||['any','null'].includes(v.trim().toLowerCase()))) throw new Error('Enter up to 20 subdivision names.');
    } else {
      const token=String(value);
      if (!(def.type==='integer' ? /^\d+$/ : /^\d+(?:\.\d{1,2})?$/).test(token) || Number(value)>def.maximum) throw new Error(`Enter a valid nonnegative value for ${def.label}.`);
    }
  }
  for (const stem of ['Price','Bedrooms','Bathrooms','Sqft','YearBuilt','LotSizeAcres','DaysOnMarket','FullBathrooms','HalfBathrooms','GarageSpaces','CarportSpaces','ParkingSpaces']) {
    if (present(params['min'+stem]) && present(params['max'+stem]) && Number(params['min'+stem])>Number(params['max'+stem])) throw new Error(`The minimum ${stem.replace(/([A-Z])/g,' $1').trim().toLowerCase()} exceeds the maximum.`);
  }
  for (const stem of ['Garage','Carport']) {
    const flag=params['has'+stem];
    if (([false,'false'].includes(flag)&&Number(params['min'+stem+'Spaces'])>0)||([true,'true'].includes(flag)&&present(params['max'+stem+'Spaces'])&&Number(params['max'+stem+'Spaces'])===0)) throw new Error(`The ${stem.toLowerCase()} requirements conflict.`);
  }
  if ([false,'false'].includes(params.hasGarage)&&[true,'true'].includes(params.hasAttachedGarage)) throw new Error('An attached garage conflicts with no garage.');
  if (params.subdivision?.length && !params.state && !params.originatingSystemName) throw new Error('Choose a state for the subdivision search.');
}
export async function readSearchError(response) {
  try {
    const data=await response.json();
    if (['FILTER_UNAVAILABLE','FILTER_NOT_READY'].includes(data.code)) return `The ${definitions[data.parameter]?.label || 'requested filter'} cannot currently be verified in this market. Your requirements have not been removed.`;
    if (response.status===409) return 'This result page has expired or changed. Start the search again.';
    if (response.status===400) return data.message || 'Check your search filters.';
  } catch (_) { /* Preserve distinction between failure and empty inventory. */ }
  return 'MLS search unavailable';
}
const cache = new Map();
export async function getFilterOptions(scope, {prefix, fetchImpl=fetch}={}) {
  const query=new URLSearchParams();
  for (const key of ['state','originatingSystemName','propertyType']) {
    if (present(scope[key])) (Array.isArray(scope[key])?scope[key]:[scope[key]]).forEach(v=>query.append(key,v));
  }
  if (!query.has('state')&&!query.has('originatingSystemName')) throw new Error('Choose a state to check filter availability.');
  if (prefix!==undefined) { query.set('field','subdivision');query.set('q',prefix); }
  const key=query.toString();const existing=cache.get(key);
  if (existing && existing.until>Date.now()) return existing.promise;
  const promise=(async()=>{
    const response=await fetchImpl(`https://tophamtechbe.com/api/treas/idx/v1/property-filter-options?${query}`);
    if (!response.ok) throw new Error(await readSearchError(response));
    const data=await response.json();
    if (data.contract_version!=='robust-search-v1'||!Array.isArray(data.enabled_parameters)) throw new Error('Filter options are unavailable.');
    const expires=Date.parse(data.coverage_as_of)+900000;
    if (!Number.isFinite(expires)||expires<=Date.now()) throw new Error('Filter options are being refreshed. Try again shortly.');
    const entry=cache.get(key);if(entry) entry.until=Math.min(Date.now()+300000,expires);
    return data;
  })();
  cache.set(key,{until:Date.now()+300000,promise});
  while(cache.size>128) cache.delete(cache.keys().next().value);
  try { return await promise; } catch(error) { cache.delete(key);throw error; }
}
